import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { V2Provider } from "./context/V2Context";
import { DensityProvider } from "./context/DensityContext";
import Layout from "./components/Layout";
import { applyDensity, loadDensity } from "./components/ProfileMenu";
// Apply density before any page renders so the header uses the right height.
applyDensity(loadDensity());
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import ClientsWorkspace from "./pages/ClientsWorkspace";
import Census from "./pages/Census";
import Caseload from "./pages/Caseload";
import Scheduler from "./pages/Scheduler";
import Holidays from "./pages/Holidays";
import UsersPage from "./pages/UsersPage";
import UserProfile360 from "./pages/UserProfile360";
import Preferences from "./pages/Preferences";
import AllClients from "./pages/AllClients";
import Tasks from "./pages/Tasks";
import InsuranceCompanies from "./pages/InsuranceCompanies";
import InsuranceDetail from "./pages/InsuranceDetail";
import Security from "./pages/Security";
import Placeholder from "./pages/Placeholder";
import ExternalEntities from "./pages/ExternalEntities";
import OutcomeMeasures from "./pages/OutcomeMeasures";
import Asam from "./pages/Asam";
import BehaviorTracker from "./pages/BehaviorTracker";
import LettersMessaging from "./pages/LettersMessaging";
import QualityMeasures from "./pages/QualityMeasures";
import PrintCenter from "./pages/PrintCenter";
import Settings from "./pages/Settings";
import HelpCenter from "./pages/HelpCenter";
import LegalPage from "./pages/LegalPage";
import TaskDetail from "./pages/TaskDetail";
import IntakeTaskDetail from "./pages/engagement/IntakeTaskDetail";
import EntranceSurvey from "./pages/engagement/EntranceSurvey";
import ReferralList from "./pages/engagement/ReferralList";
import ReferralWizard from "./pages/engagement/ReferralWizard";
import IntakeTasks from "./pages/engagement/IntakeTasks";
import IntakeList from "./pages/engagement/IntakeList";
import IntakeWizard from "./pages/engagement/IntakeWizard";
import AllDocuments from "./pages/clinical/AllDocuments";
import ProgressNotes from "./pages/clinical/ProgressNotes";
import ProgressNoteEditor from "./pages/clinical/ProgressNoteEditor";
import TreatmentPlans from "./pages/clinical/TreatmentPlans";
import TreatmentPlanEditor from "./pages/clinical/TreatmentPlanEditor";
import ConsentForms from "./pages/clinical/ConsentForms";
import Assessments from "./pages/clinical/Assessments";
import AssessmentFill from "./pages/clinical/AssessmentFill";
import CrisisPlans from "./pages/clinical/CrisisPlans";
import CrisisPlanEditor from "./pages/clinical/CrisisPlanEditor";
import UnlockRequestsAdmin from "./pages/admin/UnlockRequestsAdmin";
import ConfigurationLibrary from "./pages/admin/ConfigurationLibrary";
import DataRetention from "./pages/admin/DataRetention";
import PlatformConsole from "./pages/PlatformConsole";
import Billing from "./pages/Billing";
import { PaymentSuccess, PaymentCancel } from "./pages/PaymentResult";
import ClientConsole from "./pages/ClientConsole";
import Groups from "./pages/Groups";
import Group360 from "./pages/Group360";
import ClinicalForms from "./pages/ClinicalForms";
import RevenueCycle from "./pages/RevenueCycle";
import SurveyFill from "./pages/SurveyFill";
import Reports from "./pages/Reports";
import Telehealth from "./pages/Telehealth";
import Equipment from "./pages/Equipment";
import Requests from "./pages/Requests";
import OverdueCenter from "./pages/OverdueCenter";
import OrderSignOff from "./pages/OrderSignOff";
import PrescriberDashboard from "./pages/PrescriberDashboard";
import RefillCallQueue from "./pages/RefillCallQueue";
import HandoutReminders from "./pages/HandoutReminders";
import ChartGaps from "./pages/ChartGaps";
import SignatureAudit from "./pages/SignatureAudit";
import DdiAudit from "./pages/medical/DdiAudit";
import Resources from "./pages/Resources";
import FeedbackSurveys from "./pages/FeedbackSurveys";
import PortalLayout from "./portal/PortalLayout";
import PortalDashboard from "./portal/PortalDashboard";
import PortalOnboarding from "./portal/PortalOnboarding";
import PortalAppointments from "./portal/PortalAppointments";
import PortalMedications from "./portal/PortalMedications";
import PortalConsents from "./portal/PortalConsents";
import PortalTasks from "./portal/PortalTasks";
import PortalProfile from "./portal/PortalProfile";
import PortalFeedback from "./portal/PortalFeedback";
import PortalEducation from "./portal/PortalEducation";
import PortalMessages from "./portal/PortalMessages";
import PortalPharmacies from "./portal/PortalPharmacies";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8] text-[#84675B]">Loading Cocoa EHR…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function LoginGate() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#3E2723]" />;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <V2Provider>
          <DensityProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginGate />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/survey/:token" element={<SurveyFill />} />
            <Route path="/portal" element={<PortalLayout />}>
              <Route index element={<PortalDashboard />} />
              <Route path="onboarding" element={<PortalOnboarding />} />
              <Route path="appointments" element={<PortalAppointments />} />              <Route path="medications" element={<PortalMedications />} />
              <Route path="documents" element={<PortalConsents />} />
              <Route path="feedback" element={<PortalFeedback />} />
              <Route path="education" element={<PortalEducation />} />
              <Route path="messages" element={<PortalMessages />} />
              <Route path="pharmacies" element={<PortalPharmacies />} />
              <Route path="tasks" element={<PortalTasks />} />
              <Route path="profile" element={<PortalProfile />} />
            </Route>
            <Route path="/clients/:clientId/console" element={<Protected><ClientConsole /></Protected>} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/clients" element={<ClientsWorkspace />} />
              <Route path="/clients/:id" element={<ClientsWorkspace />} />
              <Route path="/census" element={<Census />} />
              <Route path="/caseload" element={<Caseload />} />
              <Route path="/prescriber-dashboard" element={<PrescriberDashboard />} />
              <Route path="/refill-call-queue" element={<RefillCallQueue />} />
              <Route path="/handout-reminders" element={<HandoutReminders />} />
              <Route path="/scheduler" element={<Scheduler />} />
              <Route path="/holidays" element={<Holidays />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/users/:id" element={<Navigate to="/settings/users" replace />} />
              <Route path="/preferences" element={<Preferences />} />
              <Route path="/clients/all" element={<AllClients />} />
              <Route path="/staff/:id" element={<UserProfile360 />} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/tasks/:id" element={<TaskDetail />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/legal" element={<Navigate to="/legal/hipaa" replace />} />
              <Route path="/legal/:doc" element={<LegalPage />} />
              <Route path="/insurance-companies" element={<InsuranceCompanies />} />
              <Route path="/insurance-companies/:id" element={<InsuranceDetail />} />
              <Route path="/security" element={<Security />} />
              <Route path="/engagement/referrals" element={<ReferralList />} />
              <Route path="/engagement/referrals/new" element={<ReferralWizard />} />
              <Route path="/engagement/referrals/:id/edit" element={<ReferralWizard />} />
              <Route path="/engagement/intake-tasks" element={<IntakeTasks />} />
              <Route path="/engagement/intake-tasks/:id" element={<IntakeTaskDetail />} />
              <Route path="/engagement/intake" element={<IntakeList />} />
              <Route path="/engagement/intake/new" element={<IntakeWizard />} />
              <Route path="/engagement/intake/:id/edit" element={<IntakeWizard />} />
              <Route path="/engagement/insurance-verification" element={<Placeholder title="Insurance Verification" />} />
              <Route path="/engagement/consent-forms" element={<Navigate to="/clinical/consents" replace />} />
              <Route path="/clinical/documents" element={<AllDocuments />} />
              <Route path="/clinical/progress-notes" element={<ProgressNotes />} />
              <Route path="/clinical/progress-notes/new" element={<ProgressNoteEditor />} />
              <Route path="/clinical/progress-notes/:id" element={<ProgressNoteEditor />} />
              <Route path="/clinical/treatment-plans" element={<TreatmentPlans />} />
              <Route path="/clinical/treatment-plans/new" element={<TreatmentPlanEditor />} />
              <Route path="/clinical/treatment-plans/:id" element={<TreatmentPlanEditor />} />
              <Route path="/clinical/consents" element={<ConsentForms />} />
              <Route path="/clinical/assessments" element={<Assessments />} />
              <Route path="/clinical/assessments/:id" element={<AssessmentFill />} />
              <Route path="/clinical/crisis-plans" element={<CrisisPlans />} />
              <Route path="/clinical/crisis-plans/new" element={<CrisisPlanEditor />} />
              <Route path="/clinical/crisis-plans/:id" element={<CrisisPlanEditor />} />
              <Route path="/admin/unlock-requests" element={<UnlockRequestsAdmin />} />
              <Route path="/admin/configuration" element={<ConfigurationLibrary />} />
              <Route path="/admin/data-retention" element={<DataRetention />} />
              <Route path="/engagement/orientation-checklist" element={<Placeholder title="Orientation Checklist" />} />
              <Route path="/engagement/entrance-survey" element={<EntranceSurvey />} />
              <Route path="/external-entities" element={<ExternalEntities />} />
              <Route path="/groups" element={<Groups />} />
              <Route path="/group360/:id" element={<Group360 />} />
              <Route path="/clinical-forms" element={<ClinicalForms />} />
              <Route path="/revenue" element={<RevenueCycle />} />
              <Route path="/outcome-measures" element={<OutcomeMeasures />} />
              <Route path="/asam" element={<Asam />} />
              <Route path="/behavior-tracker" element={<BehaviorTracker />} />
              <Route path="/letters" element={<LettersMessaging />} />
              <Route path="/quality-measures" element={<QualityMeasures />} />
              <Route path="/print" element={<PrintCenter />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/:section" element={<Settings />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/telehealth" element={<Telehealth />} />
              <Route path="/equipment" element={<Equipment />} />
              <Route path="/requests" element={<Requests />} />
              <Route path="/overdue" element={<OverdueCenter />} />
              <Route path="/orders/sign-off" element={<OrderSignOff />} />
              <Route path="/chart-gaps" element={<ChartGaps />} />
              <Route path="/signatures" element={<SignatureAudit />} />
              <Route path="/medical/ddi-audit" element={<DdiAudit />} />
              <Route path="/portal-feedback" element={<FeedbackSurveys />} />
              <Route path="/platform" element={<PlatformConsole />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/configuration/programs" element={<Navigate to="/settings/programs" replace />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/module/:name" element={<Placeholder />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
          </DensityProvider>
        </V2Provider>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}
